/**
 * Agent Actions Hook
 *
 * Manages agent actions (tasks, workflows, outcomes) using React Query
 */

import type {
  AgentActionQuery,
  CreateAgentAction,
  PaginatedAgentActionsResponse,
  UpdateAgentAction,
} from '@monorepo/shared/schemas/agents'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { agentActionsService } from '../services/agentActionsService'
import { agentKeys } from './useAgents'

// Query keys
export const agentActionKeys = {
  all: ['agent-actions'] as const,
  lists: () => [...agentActionKeys.all, 'list'] as const,
  list: (agentId: string, filters?: AgentActionQuery) =>
    [...agentActionKeys.lists(), agentId, filters] as const,
  details: () => [...agentActionKeys.all, 'detail'] as const,
  detail: (agentId: string, actionId: string) =>
    [...agentActionKeys.details(), agentId, actionId] as const,
}

/**
 * Fetch actions for an agent
 */
export function useAgentActions(agentId: string, query?: AgentActionQuery) {
  return useQuery({
    queryKey: agentActionKeys.list(agentId, query),
    queryFn: () => agentActionsService.getActions(agentId, query),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single action
 */
export function useAgentAction(agentId: string, actionId: string) {
  return useQuery({
    queryKey: agentActionKeys.detail(agentId, actionId),
    queryFn: () => agentActionsService.getAction(agentId, actionId),
    enabled: !!agentId && !!actionId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Create new action for an agent
 */
export function useCreateAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      agentId,
      data,
    }: {
      agentId: string
      data: CreateAgentAction
    }) => agentActionsService.createAction(agentId, data),
    onSuccess: (newAction, variables) => {
      // Invalidate actions list for this agent
      queryClient.invalidateQueries({
        queryKey: agentActionKeys.lists(),
      })

      // Invalidate agent detail to refresh actions count
      queryClient.invalidateQueries({
        queryKey: agentKeys.detail(variables.agentId),
      })

      // Invalidate the agents list to refresh signal counts in agent cards
      queryClient.invalidateQueries({
        queryKey: agentKeys.lists(),
      })

      toast.success(
        `Action "${newAction.action_name}" has been created successfully.`
      )
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Update existing action
 */
export function useUpdateAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      agentId,
      actionId,
      data,
    }: {
      agentId: string
      actionId: string
      data: UpdateAgentAction
    }) => agentActionsService.updateAction(agentId, actionId, data),
    onSuccess: (updatedAction, variables) => {
      // Update specific action in cache
      queryClient.setQueryData(
        agentActionKeys.detail(variables.agentId, variables.actionId),
        updatedAction
      )

      // Update actions list cache
      const matchingLists = queryClient.getQueriesData({
        queryKey: agentActionKeys.lists(),
      })
      for (const [key] of matchingLists) {
        queryClient.setQueryData(key, (existing: unknown) => {
          if (
            existing &&
            typeof existing === 'object' &&
            'data' in existing &&
            Array.isArray((existing as { data: unknown }).data)
          ) {
            const paginated = existing as PaginatedAgentActionsResponse
            return {
              ...paginated,
              data: paginated.data.map(a =>
                a.id === updatedAction.id ? updatedAction : a
              ),
            }
          }
          return existing
        })
      }

      // Invalidate lists to refresh
      queryClient.invalidateQueries({
        queryKey: agentActionKeys.lists(),
      })

      // Also invalidate the specific action detail query to ensure it refetches
      queryClient.invalidateQueries({
        queryKey: agentActionKeys.detail(variables.agentId, variables.actionId),
      })

      // Invalidate agent detail to refresh action data in agent view
      queryClient.invalidateQueries({
        queryKey: agentKeys.detail(variables.agentId),
      })

      // Invalidate the agents list to refresh signal counts in agent cards
      queryClient.invalidateQueries({
        queryKey: agentKeys.lists(),
      })

      toast.success(
        `Action "${updatedAction.action_name}" has been updated successfully.`
      )
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Delete action
 */
export function useDeleteAgentAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      agentId,
      actionId,
    }: {
      agentId: string
      actionId: string
    }) => agentActionsService.deleteAction(agentId, actionId),
    onSuccess: (_, variables) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: agentActionKeys.detail(variables.agentId, variables.actionId),
      })

      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: agentActionKeys.lists(),
      })

      // Invalidate agent detail to refresh actions count
      queryClient.invalidateQueries({
        queryKey: agentKeys.detail(variables.agentId),
      })

      // Invalidate the agents list to refresh signal counts in agent cards
      queryClient.invalidateQueries({
        queryKey: agentKeys.lists(),
      })

      toast.success('Action has been deleted successfully.')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
